import { qaAgentsProject } from './qaProposal';
import { rendererProject } from './rendererReport';

// Updated resume: Vaibhav_Lehra_Resume.pdf, supplied by the owner.
// Metrics are reported observations, not independently reproduced benchmarks.
// PIM attribution follows the owner's explicit correction: Siddharth Juyal built
// the project; Vaibhav collaborated. This supersedes the resume's ownership wording.
// Diagram nodes are conceptual summaries of documented work, not infrastructure maps.

export const profile = {
  name: 'Vaibhav Lehra',
  role: 'Senior Python Engineer',
  location: 'Delhi NCR, India',
  email: 'vaibhavlehra@gmail.com',
  github: 'https://github.com/vlehra',
  linkedin: 'https://www.linkedin.com/in/vaibhavlehra/',
  website: 'https://vaibhavlehra.com',
  resume: '/Vaibhav_Lehra_Resume.pdf',
  positioning: 'I make data-heavy Python systems faster, leaner, and safer to change.',
  introduction: 'I build and optimize distributed services, from video rendering to learning-platform backends. My work connects performance improvements, production ownership, and the architecture of human-gated QA agents.',
  experience: '6+ years',
  mentoredEngineers: '10',
  education: {
    degree: 'B.Tech · Electrical and Electronics Engineering',
    institution: 'Guru Tegh Bahadur Institute of Technology, GGSIPU',
    location: 'Delhi',
    year: '2018',
  },
};

const projectRecords = [
  {
    id: 'pim',
    category: 'Data systems',
    title: 'Millions of products. Two gigabytes of memory.',
    subtitle: 'Product Information Management',
    company: 'Tyroo Technologies',
    period: 'Feb 2023 — Present',
    status: 'Collaboration · built by Siddharth Juyal',
    summary: 'Siddharth Juyal built this PIM ingestion rebuild at Tyroo; I was a collaborator. The two-pass Polars pipeline processes multi-million-product catalogs within 2 GB of RAM, with field-level parity checks. This study explains his rebuild and the related team work.',
    collaboration: {
      creator: 'Siddharth Juyal',
      role: 'Collaborator',
      article: {
        title: 'From 16 GB to 2 GB: How We Rebuilt Our Product Ingestion Pipeline and Cut Infra Cost by ~85%',
        url: 'https://medium.com/@siddharthjuyal333/from-16-gb-to-2-gb-how-we-rebuilt-our-product-ingestion-pipeline-and-cut-infra-cost-by-85-eb9bfadada8e',
        platform: 'Medium',
      },
    },
    measurementNote: 'These are reported project and team results. The ingestion rebuild was led and built by Siddharth Juyal; my role was collaborator. The figures describe the individual comparisons below.',
    stack: ['Python', 'Polars', 'SQS', 'MySQL', 'Redis', 'S3'],
    headlineMetric: { value: '7.2M', label: 'products · Siddharth Juyal’s rebuild' },
    metrics: [
      { value: '6.2 GB', label: 'feed size', context: '7.2M products processed on 2 vCPU / 2 GB in the replacement ingestion pilot.' },
      { value: '2 GB', label: 'memory envelope', context: 'Both 4.1 GB / 2.8M-product and 6.2 GB / 7.2M-product feeds processed within the supplied resource limit.' },
      { value: '19', label: 'hostile edge cases', context: 'Parity corpus tested alongside real feeds and corrupted encodings; field-level results were byte-identical.' },
      { value: '≈2×', label: 'SQS worker throughput', context: 'Separate worker improvement using long polling and cached clients / queue URLs; not an ingestion speedup.' },
    ],
    stages: [
      {
        id: 'constraint',
        label: 'Constraint',
        title: 'The catalog outgrew the memory model.',
        body: 'The previous ingestion engine held whole feeds in memory at roughly 15× the file size. It exhausted 6.5 GB in seconds, leaving an S3 chunk-splitting workaround between the feed and processing.',
        notes: ['Whole-feed allocation at approximately 15× file size', 'Target envelope: 2 vCPU / 2 GB', 'Keep field-level output compatible'],
        nodes: ['Large product feed', 'Whole-feed allocation', 'Memory exhaustion'],
      },
      {
        id: 'decision',
        label: 'Decision',
        title: 'Change the ingestion shape.',
        body: 'Siddharth Juyal built the replacement ingestion pipeline using two-pass streaming with Polars. Processing at flat memory removed the need for the S3 chunk-splitting workaround. I was a collaborator on this project.',
        notes: ['Two-pass Polars pipeline', 'Stream instead of retaining the whole feed', 'Pilot the replacement before broader cutover'],
        nodes: ['Product feed', 'Two-pass streaming', 'Processed catalog'],
      },
      {
        id: 'verification',
        label: 'Verification',
        title: 'Prove the data still matches.',
        body: 'The project’s field-level parity harness compared both engines. It produced byte-identical results on real feeds, corrupted encodings, and a 19-case hostile edge corpus, checking correctness separately from the memory improvement. These are validation results for Siddharth’s rebuild.',
        notes: ['Compare old and replacement engines', 'Real feeds plus corrupted encodings', '19-case hostile edge corpus'],
        nodes: ['Same input feed', 'Both engines', 'Field-level parity'],
      },
      {
        id: 'result',
        label: 'Result',
        title: 'A smaller memory envelope, with matching output.',
        body: 'Siddharth’s ingestion rebuild processed 4.1 GB / 2.8M-product and 6.2 GB / 7.2M-product feeds on 2 vCPU / 2 GB in the reported pilot. Related team improvements to SQS roughly doubled per-worker throughput. These are project outcomes; my role was collaborator.',
        notes: ['7.2M products within 2 GB RAM', 'Byte-identical parity results', 'Approximately doubled SQS worker throughput'],
        nodes: ['6.2 GB feed', '2 GB processing envelope', 'Verified output'],
      },
    ],
    sections: [
      {
        title: 'Siddharth’s project. A collaboration at Tyroo.',
        paragraphs: [
          'Siddharth Juyal led and built the PIM ingestion rebuild. I was a collaborator on the project, and the credit for the rebuild belongs to him.',
          'The engineering story below describes the project’s constraints, approach, and reported results. Siddharth’s own article is linked above for his account of the work.',
        ],
        bullets: [],
      },
      {
        title: 'The problem was allocation, not just feed size.',
        paragraphs: [
          'PIM ingests multi-million-product catalog feeds in Tyroo’s product-data platform. This study follows the ingestion rebuild built by Siddharth Juyal, which I participated in as a collaborator.',
          'The previous engine’s whole-feed approach used roughly 15× the input file size in memory and exhausted 6.5 GB in seconds. An S3 chunk-splitting workaround supported that constraint. The replacement needed to change the resource profile while preserving the data produced by the existing engine.',
        ],
        bullets: ['4.1 GB feed containing 2.8M products', '6.2 GB feed containing 7.2M products', 'Replacement pilot resource envelope: 2 vCPU / 2 GB'],
      },
      {
        title: 'Streaming with a separate correctness check.',
        paragraphs: [
          'Siddharth’s replacement uses a two-pass Polars streaming DataFrame pipeline at flat memory. It processed the documented feeds within 2 GB of RAM and removed the need for the S3 chunk-splitting workaround.',
          'A field-level parity harness compared the existing and replacement engines before the proposed cutover. The project’s checks covered real feeds, corrupted encodings, and a 19-case hostile edge corpus, producing byte-identical results.',
        ],
        bullets: ['Two-pass streaming ingestion', 'Field-level comparisons between both engines', 'Real-data and hostile-input validation'],
      },
      {
        title: 'The worker path needed attention too.',
        paragraphs: [
          'Related team work improved event-driven SQS workers. Long polling and cached clients / queue URLs roughly doubled per-worker throughput.',
          'The related service work also included stronger MySQL and Redis reconnect handling and a Python 3.9 to 3.11 migration. These project changes address worker efficiency, connection recovery, and the runtime independently of the ingestion memory result.',
        ],
        bullets: ['SQS long polling', 'Cached clients and queue URLs', 'MySQL / Redis reconnect handling', 'Python 3.9 → 3.11'],
      },
      {
        title: 'A correctness-led pilot.',
        paragraphs: [
          'Siddharth’s ingestion rebuild pairs a smaller resource envelope with explicit output-compatibility checks. Worker throughput, reconnect handling, and the runtime migration form a separate set of team improvements to the event-driven processing path.',
        ],
        bullets: [],
      },
    ],
    related: ['renderer', 'qa-agents'],
  },
  rendererProject,
  qaAgentsProject,
  {
    id: 'myclassroom',
    category: 'Platform engineering',
    title: 'The backend behind a live-learning platform.',
    subtitle: 'Myclassroom',
    company: 'Myclassroom Learning Pvt. Ltd.',
    period: 'Sept 2020 — Oct 2022',
    status: 'Delivered platform and engineering support',
    summary: 'I architected the PostgreSQL model and built the DRF API layer for live classes, test series, video lectures, and student analytics. I standardized EC2 deployment and led the engineering support practices needed to maintain the platform.',
    stack: ['PostgreSQL', 'Django REST Framework', 'React clients', 'AWS EC2', 'NGINX', 'Gunicorn'],
    headlineMetric: { value: 'End to end', label: 'data model to production support' },
    metrics: [
      { value: 'Data model', label: 'learning-platform foundation', context: 'PostgreSQL model for live classes, test series, video lectures, and student performance analytics.' },
      { value: 'API layer', label: 'web and mobile clients', context: 'DRF CRUD, filtering, pagination, and authentication APIs powering React clients.' },
      { value: 'Deployment', label: 'multiple EC2 instances', context: 'Standardized deployment with NGINX and Gunicorn.' },
      { value: 'Support', label: 'triage and escalation', context: 'Built and led engineering support, mentored engineers, and established production-issue processes.' },
    ],
    stages: [
      {
        id: 'constraint',
        label: 'Constraint',
        title: 'Several learning experiences, one platform foundation.',
        body: 'The platform spans live classes, test series, video lectures, and student performance analytics, with React web and mobile clients consuming the backend.',
        notes: ['Live classes and video lectures', 'Test series and student analytics', 'React web and mobile clients'],
        nodes: ['Learning experiences', 'Shared data model', 'Web + mobile clients'],
      },
      {
        id: 'decision',
        label: 'Decision',
        title: 'Build the model and the API together.',
        body: 'I architected the PostgreSQL data model and built the DRF API layer with CRUD, filtering, pagination, and authentication. Deployment was standardized across multiple EC2 instances with NGINX and Gunicorn.',
        notes: ['PostgreSQL data model', 'DRF API capabilities', 'EC2 / NGINX / Gunicorn deployment'],
        nodes: ['PostgreSQL', 'DRF APIs', 'React clients'],
      },
      {
        id: 'verification',
        label: 'Verification',
        title: 'Carry ownership into production support.',
        body: 'I built and led the engineering support function, mentored engineers, and established triage and escalation for production issues.',
        notes: ['Engineering support function', 'Engineer mentoring', 'Production triage and escalation'],
        nodes: ['Production issue', 'Engineering triage', 'Escalation + support'],
      },
      {
        id: 'result',
        label: 'Result',
        title: 'A backend delivered through to operations.',
        body: 'The work connected the platform’s PostgreSQL model and DRF APIs to React clients, standardized multi-instance deployment, and an engineering support function.',
        notes: ['Learning-platform data model', 'APIs for web and mobile', 'Deployment and support practices'],
        nodes: ['Platform backend', 'Multi-instance deployment', 'Engineering support'],
      },
    ],
    sections: [
      {
        title: 'Model the learning platform.',
        paragraphs: [
          'As a Backend Developer at Myclassroom Learning Pvt. Ltd., I architected the PostgreSQL data model for live classes, test series, video lectures, and student performance analytics.',
        ],
        bullets: ['Live classes', 'Test series', 'Video lectures', 'Student performance analytics'],
      },
      {
        title: 'Connect web and mobile experiences.',
        paragraphs: [
          'I built the Django REST Framework API layer powering React web and mobile clients. Its documented capabilities include CRUD, filtering, pagination, and authentication.',
        ],
        bullets: ['CRUD operations', 'Filtering and pagination', 'Authentication', 'React web and mobile clients'],
      },
      {
        title: 'Deployment was part of delivery.',
        paragraphs: [
          'I standardized deployment across multiple EC2 instances using NGINX and Gunicorn. I also built and led the engineering support function, mentoring engineers and establishing triage and escalation for production issues.',
        ],
        bullets: ['Multiple AWS EC2 instances', 'NGINX and Gunicorn', 'Engineering support and mentoring', 'Production triage and escalation'],
      },
    ],
    related: ['renderer', 'qa-agents'],
  },
];

// Numbers belong to each ownership group. Reordering or adding a collaboration
// must never change where the owned-work sequence starts.
const numberProjects = items => items.map((project, index) => ({
  ...project,
  number: String(index + 1).padStart(2, '0'),
}));

export const ownedProjects = numberProjects(projectRecords.filter(project => !project.collaboration));
export const collaborationProjects = numberProjects(projectRecords.filter(project => project.collaboration));
export const projects = [...ownedProjects, ...collaborationProjects];

export const experience = [
  {
    company: 'Tyroo Technologies',
    role: 'Software Development Engineer II',
    period: 'Feb 2023 — Present',
    location: 'Gurugram',
    description: 'Work on distributed services across Dynamic Product Ads and product catalogs: renderer optimization, schema and API design, Docker / Kubernetes deployment, CI, monitoring, and production support. Architect a human-gated AI QA process, mentor a 10-engineer team, and lead reviewed LLM-assisted development workflows. Shipped a React / TypeScript integration end to end.',
    projectIds: ['renderer', 'qa-agents', 'pim'],
  },
  {
    company: 'Qricle',
    role: 'Backend Developer',
    period: 'Oct 2022 — Dec 2022',
    location: 'Gurugram',
    description: 'Backend Developer at Qricle.',
    projectIds: [],
  },
  {
    company: 'Myclassroom Learning Pvt. Ltd.',
    role: 'Backend Developer',
    period: 'Sept 2020 — Oct 2022',
    location: 'Noida',
    description: 'Architected the PostgreSQL learning-platform model and built DRF APIs for React web and mobile clients. Standardized EC2 deployment with NGINX and Gunicorn; built and led engineering support, mentored engineers, and established production triage and escalation.',
    projectIds: ['myclassroom'],
  },
];

export const capabilities = [
  {
    title: 'Data & performance',
    items: [
      { name: 'Memory profiling & leak diagnosis', projectId: 'renderer' },
      { name: 'Redis & asset caching', projectId: 'renderer' },
      { name: 'Pandas · DataFrame pipelines' },
      { name: 'Polars · ingestion collaboration', projectId: 'pim' },
      { name: 'Field-level parity · team study', projectId: 'pim' },
    ],
  },
  {
    title: 'Backend & distributed systems',
    items: [
      { name: 'PostgreSQL · data modelling', projectId: 'myclassroom' },
      { name: 'Django REST Framework · APIs', projectId: 'myclassroom' },
      { name: 'Python · Django · Flask · FastAPI' },
      { name: 'Celery · RabbitMQ · async services' },
      { name: 'MongoDB · Elasticsearch · SQL' },
      { name: 'OAuth 2.0 · JWT · permissions' },
      { name: 'SQS · team worker study', projectId: 'pim' },
      { name: 'MySQL & Redis reconnect handling · team study', projectId: 'pim' },
    ],
  },
  {
    title: 'Delivery & operations',
    items: [
      { name: 'Kubernetes · render workloads', projectId: 'renderer' },
      { name: 'EC2 · NGINX · Gunicorn', projectId: 'myclassroom' },
      { name: 'Production triage & mentoring', projectId: 'myclassroom' },
      { name: 'AWS · Docker · Linux' },
      { name: 'Jenkins · GitLab CI' },
      { name: 'React · Redux · TypeScript' },
      { name: 'Terraform · working knowledge' },
      { name: 'GCP · transferable cloud experience' },
    ],
  },
  {
    title: 'AI & engineering practice',
    items: [
      { name: 'Multi-agent orchestration · architecture', projectId: 'qa-agents' },
      { name: 'RAG · retrieval-grounded test generation', projectId: 'qa-agents' },
      { name: 'Human QA and release gates · design', projectId: 'qa-agents' },
      { name: 'Claude Code · reviewed agentic workflows' },
      { name: 'Prompt engineering · MCP workflows' },
      { name: 'Agile · code reviews · POCs & pilots' },
    ],
  },
];
